import { v4 as uuidv4 } from 'uuid';

/**
 * `client_request_id` is the ONLY protection against a double charge that NMI
 * offers — the gateway has none of its own. Rules from the backend contract:
 *
 *   - retrying the SAME payment (status `error`)    -> reuse the id
 *   - offering a DIFFERENT card (status `declined`) -> new id (and new token)
 *   - `pending`                                    -> never retry, poll instead
 *
 * Ids are SCOPED per payment: the subscription checkout and each upsell are
 * different payments, so they must never share a key — reusing one across them
 * would let the gateway treat an upsell as a duplicate of the subscription (or
 * silently drop it).
 *
 * Stored in sessionStorage rather than in memory so a page reload in the middle
 * of a failed attempt still reuses the id instead of charging twice.
 */
const STORAGE_PREFIX = 'tailoredpay_client_request_id';

export const TailoredpayRequestScope = {
  CHECKOUT: 'checkout',
  /** One scope per product, so two different upsells never share a key. */
  oneTime: (productCode?: string) => `one-time:${productCode || 'default'}`,
} as const;

const storageKey = (scope: string) => `${STORAGE_PREFIX}:${scope}`;

/** Returns the current attempt's id for this scope, creating one if absent. */
export const getTailoredpayRequestId = (scope: string): string => {
  try {
    const existing = sessionStorage.getItem(storageKey(scope));
    if (existing) {
      return existing;
    }

    const id = uuidv4();
    sessionStorage.setItem(storageKey(scope), id);
    return id;
  } catch (e) {
    // Private-mode Safari can throw on sessionStorage. A fresh id is still
    // better than no payment; the trade-off is that a reload could re-charge.
    return uuidv4();
  }
};

/**
 * Starts a NEW payment attempt for this scope. Call after a `declined` result
 * (the user is trying a different card) and after a success — NOT after a
 * technical `error`, where the same id has to be replayed.
 */
export const resetTailoredpayRequestId = (scope: string) => {
  try {
    sessionStorage.removeItem(storageKey(scope));
  } catch (e) {
    // no-op
  }
};
